import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { goBack, replaceRoute } from '../../app/navigation';
import { agreementSigned, defaultAgreement, type V2Agreement } from '../../domain/agreement';
import { emptyHousePlan, planCounts, type HousePlan } from '../../domain/housePlan';
import { PROPERTY_TYPE_ORDER, type PropertyType, type StartMode } from '../../domain/inspection';
import { draftContextSubject, formalWorkflowGate } from '../../domain/inspectionContext';
import type { HeatingFuel, UtilitySource } from '../../domain/descriptors';
import { JURISDICTION_CODE_ORDER, isJurisdictionCode, type JurisdictionCode } from '../../domain/jurisdiction';
import { useT } from '../../i18n/i18n';
import { labelWithTag, planNamingFor, propertyTypeLabel } from '../../i18n/labels';
import { billingStore, consumeInspectionCredit, ensureBillingStarted, ensureInspectionAllowed } from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import { coverageAvailable, FREE_TRIAL_INSPECTIONS, RELEASE_LIMIT } from '../../../supabase/functions/_shared/billingPolicy';
import { boundHolderName } from '../../state/boundLicense';
import type { InspectionKind } from '../../../supabase/functions/_shared/billingPolicy';
import { createInspectionAction } from '../../state/inspectionStore';
import { releasePendingCover, setCoverPhotoAction, type PendingCover } from '../../state/coverPhoto';
import { clearNewInspectionDraft, describeDraftCover, loadDraftCover, readNewInspectionDraft, removeDraftCover, storeDraftCover, writeNewInspectionDraft, type NewInspectionDraft } from '../../state/newInspectionDraft';
import { PurchaseSheet } from '../billing/PurchaseSheet';
import { inspectorRefFrom, readInspectorProfile, rememberInspectorName } from '../../state/preferences';
import { Banner, Button, ChoiceCard, ConfirmSheet, Field, FormRow, Icon, PickerRow, RowInput, SheetShell, TextInput, TopBar, showToast } from '../../ui/components';
import { useStickyFooterClearance } from '../../ui/stickyFooter';
import { AddressRow } from './AddressAutocomplete';
import { CoverPhotoField, CoverSummary } from './CoverPhotoField';
import { AgreementCard, type AgreementSubject } from './AgreementCard';
import { LicenceSheet } from './LicenceSheet';
import { PropertyLayoutCard } from './PropertyLayoutCard';
import { PropertyFactRows } from './PropertyFactRows';

// New Inspection (Owner task book 2026-09-04 §5–§8; Round 2 §C / §D / §E; Owner-selected design
// 2026-09-14, Figma 191:7 "NEW · B · 4-Step Wizard", Owner answers Q1 = one step per screen, Q2 = the
// grey rows). Four steps under one progress bar — 1 房屋 · 2 客户 · 时间 · 验屋师 · 3 房屋布局 ·
// 4 验屋协议 · 起步方式 — with the address + cover summary card at the top of every step. Any segment
// of the bar jumps to its step; ‹ goes back one step (leaves from step 1); Next never blocks. NOTHING
// is pre-seeded: property type, year, basement, floors, rooms and spaces are all unset until the
// inspector sets them (§D) — only the whole-house systems are inspection coverage and stay on.
// No inspection record exists until Create; what the inspector has typed so far is kept as a DRAFT of the
// form (src/state/newInspectionDraft.ts, Owner device test 2026-09-22) and comes back when the form opens
// again — Create clears it, 清空重填 clears it and starts over.
//
// VALIDATION UX (Owner contract 2026-09-13 §29–§38): the page opens with NO error state and Create
// (step 4) stays tappable while ordinary required fields are empty. The first tap on Create validates:
// what is missing is named next to the field it belongs to and summarized above the button — tapping
// the summary jumps to the step that holds the first missing thing and brings it on screen — and every
// message clears as soon as it is fixed. Every real gate (address, layout, start mode, licence / state /
// occupancy / unit count, billing) still stands — a tap creates nothing until they all pass. Create is
// disabled only while submitting. The frozen AI notice 「AI也可能出错，所有分析结果仅供参考。」 stands
// in the footer of every step and at the bottom of the agreement text (§29 / §30).
type Step = 1 | 2 | 3 | 4;
const STEPS: readonly Step[] = [1, 2, 3, 4];
type FocusTarget = 'address' | 'layout' | 'start' | 'locality' | 'occupancy' | 'units' | 'blockers';

function toLocalInputValue(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(value: string, fallback: number): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function StepCard(props: { n: Step; title: string; sub: string; testId: string; children: ReactNode }) {
  return (
    <section className="card wizard-card" data-testid={props.testId} data-step={props.n}>
      <div className="wizard-card-head">
        <span className="wizard-card-no" aria-hidden="true">
          {props.n}
        </span>
        <div className="wizard-card-text">
          <h2 className="wizard-card-title">{props.title}</h2>
          <p className="wizard-card-sub">{props.sub}</p>
        </div>
      </div>
      {props.children}
    </section>
  );
}

// The form keeps a DRAFT of itself while it is being filled in (Owner device test 2026-09-22 of build 93:
// 「新建验屋没完成时退出，进去所有资料都没有了，要重新填写，包括封面照片也没有了」): every field, the step and the
// cover photo are written to the account's own storage as the inspector works (src/state/newInspectionDraft.ts)
// and come back the next time the form opens — after a tab switch, a Cancel, an app restart. Create clears
// the draft; 清空重填 in the note under the progress bar clears it and starts the form over (a remount).
export function NewInspectionScreen() {
  const [formKey, setFormKey] = useState(0);
  return <NewInspectionForm key={formKey} onStartOver={() => setFormKey((k) => k + 1)} />;
}

function NewInspectionForm(props: { onStartOver: () => void }) {
  const { t, tp, locale } = useT();
  const footerRef = useStickyFooterClearance();
  // Read ONCE, before any field exists: the draft is the initial value of everything below.
  const [restored] = useState(() => readNewInspectionDraft());
  const draftRestored = restored !== null;
  const [step, setStep] = useState<Step>(restored?.step ?? 1);
  const [address, setAddress] = useState(restored?.address ?? '');
  const [city, setCity] = useState(restored?.city ?? '');
  // NY F1 Round 1 (RT-04): the state is CHOSEN, never defaulted. A form default is not an
  // inspector assertion, so nothing is pre-selected and choosing is what confirms the fact. An
  // address picked from the completion list carries the state Google prints for it — that pick is
  // the inspector's choice of THAT address.
  const [state, setState] = useState<JurisdictionCode | null>(restored?.state ?? null);
  // CK-11 / PP-20: asked once, here, in the statute's own terms. Never inferred.
  const [previouslyOccupied, setPreviouslyOccupied] = useState<boolean | null>(restored?.previouslyOccupied ?? null);
  // CK-10 (NY F1 Round 5 / LR-06): dwelling units, asked once here. null = not asked (never a block).
  const [unitCount, setUnitCount] = useState<number | null>(restored?.unitCount ?? null);
  // NY F1 Round 2 (CK-01 / 197-5.8(d) / 197-5.10(a)(1)): the three property facts, asked once here.
  // Optional at creation (a legacy-style record may answer later on the details screen); never
  // defaulted, never inferred.
  const [heatingFuel, setHeatingFuel] = useState<HeatingFuel | null>(restored?.heatingFuel ?? null);
  const [waterSupplySource, setWaterSupplySource] = useState<UtilitySource | null>(restored?.waterSupplySource ?? null);
  const [wasteDisposalSource, setWasteDisposalSource] = useState<UtilitySource | null>(restored?.wasteDisposalSource ?? null);
  const [zip, setZip] = useState(restored?.zip ?? '');
  const [propertyType, setPropertyType] = useState<PropertyType | null>(restored?.propertyType ?? null);
  const [yearBuilt, setYearBuilt] = useState(restored?.yearBuilt ?? '');
  const [clientName, setClientName] = useState(restored?.clientName ?? '');
  const [clientPhone, setClientPhone] = useState(restored?.clientPhone ?? '');
  const [clientEmail, setClientEmail] = useState(restored?.clientEmail ?? '');
  const [inspectionAt, setInspectionAt] = useState(() => restored?.inspectionAt || toLocalInputValue(Date.now()));
  // State, not a one-time read: the licence can be entered over this form (LicenceSheet).
  const [profile, setProfile] = useState(() => readInspectorProfile());
  const [inspectorName, setInspectorName] = useState(restored?.inspectorName || profile.name);
  const [weather, setWeather] = useState(restored?.weather ?? '');
  const [agreement, setAgreement] = useState<V2Agreement>(() => restored?.agreement ?? defaultAgreement());
  const [plan, setPlan] = useState<HousePlan>(() => restored?.plan ?? emptyHousePlan());
  const [startMode, setStartMode] = useState<StartMode | null>(restored?.startMode ?? null);
  // Owner 2026-09-14: the report cover photo, picked here and stored the moment the inspection exists.
  const [cover, setCover] = useState<PendingCover | null>(null);
  useEffect(() => () => {
    if (cover) releasePendingCover(cover);
  }, [cover]);
  // The draft's cover comes back from the account's media store (bytes), a moment after the fields.
  useEffect(() => {
    const described = restored?.cover ?? null;
    if (!described) return;
    let alive = true;
    void loadDraftCover(described)
      .then((pending) => {
        if (!pending) return;
        if (alive) setCover(pending);
        else releasePendingCover(pending);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [restored]);
  // A picked cover goes into the draft as well (its bytes into the media store), so it survives leaving.
  function changeCover(next: PendingCover | null) {
    setCover(next);
    if (next) void storeDraftCover(next).catch(() => undefined);
    else void removeDraftCover().catch(() => undefined);
  }
  const [busy, setBusy] = useState(false);
  // A second tap that lands before the disabled state rendered must still not create twice (T26).
  const submitting = useRef(false);
  // Once the inspection exists (or the inspector started over) the old values are never written again.
  const draftClosed = useRef(false);
  const latestDraft = useRef<NewInspectionDraft | null>(null);
  // The draft is written a moment after every change, and once more when the form is left.
  useEffect(() => {
    if (draftClosed.current) return;
    const draft: NewInspectionDraft = {
      version: 1,
      savedAt: Date.now(),
      step,
      address,
      city,
      state,
      zip,
      propertyType,
      yearBuilt,
      previouslyOccupied,
      unitCount,
      heatingFuel,
      waterSupplySource,
      wasteDisposalSource,
      clientName,
      clientPhone,
      clientEmail,
      inspectionAt,
      inspectorName,
      weather,
      agreement,
      plan,
      startMode,
      cover: cover ? describeDraftCover(cover) : null
    };
    latestDraft.current = draft;
    // Checked again when the timer fires: a write scheduled just before Create must not land after the
    // draft was cleared.
    const timer = setTimeout(() => {
      if (!draftClosed.current) writeNewInspectionDraft(draft);
    }, 200);
    return () => clearTimeout(timer);
  }, [step, address, city, state, zip, propertyType, yearBuilt, previouslyOccupied, unitCount, heatingFuel, waterSupplySource, wasteDisposalSource, clientName, clientPhone, clientEmail, inspectionAt, inspectorName, weather, agreement, plan, startMode, cover]);
  useEffect(
    () => () => {
      if (!draftClosed.current && latestDraft.current) writeNewInspectionDraft(latestDraft.current);
    },
    []
  );
  function startOver() {
    draftClosed.current = true;
    void clearNewInspectionDraft().catch(() => undefined);
    props.onStartOver();
  }
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [confirmCount, setConfirmCount] = useState(false);
  const [licenceOpen, setLicenceOpen] = useState(false);
  const [localityOpen, setLocalityOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Create was tapped at least once: from then on the validation state is shown, live.
  const [attempted, setAttempted] = useState(false);
  // What to bring on screen once the step that holds it has rendered (§34).
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const billing = useStore(billingStore, (s) => s);
  // The form must know what creating this inspection will SPEND before the inspector taps Create,
  // so the entitlement is read on open (idempotent — the Home does the same).
  useEffect(() => {
    void ensureBillingStarted();
  }, []);

  const areaCount = useMemo(() => planCounts(plan).areas, [plan]);
  // NY F1 Round 1: a formal (non-Demo) inspection is created only when the jurisdiction facts and
  // PP-20 are answered. A Demo inspection is not a client engagement and is not gated.
  const gate = useMemo(
    () =>
      formalWorkflowGate({
        inspection: { ...draftContextSubject(agreement), property: { state: state ?? '', stateConfirmed: state !== null, previouslyOccupied, unitCount } },
        inspectorState: profile.licensedState
      }),
    [state, previouslyOccupied, unitCount, agreement, profile.licensedState]
  );
  const gateMessage = useMemo(() => {
    switch (gate.kind) {
      case 'clear':
      case 'nonClientTest':
        return null;
      case 'inspectorStateMissing':
        return t('setup.needLicence');
      // Named with the other missing fields (missingState / missingOccupancy below), not a second time here.
      case 'propertyStateUnconfirmed':
        return null;
      case 'unsupportedPropertyState':
        return t('setup.stateNotOpen', { state: gate.state });
      case 'stateMismatch':
        return t('gate.mismatchBody', { inspectorState: gate.inspectorState, propertyState: gate.propertyState });
      case 'occupancyUnconfirmed':
        return null;
      case 'legalReviewBlock':
        return t('setup.unitCountBlocked');
      // X-1 (Owner default 2026-09-11): an inspection may be BOOKED before it is signed — creation
      // never asks this question (the gate is evaluated here without the agreement).
      case 'agreementUnsigned':
        return null;
    }
  }, [gate, t]);
  // The ordinary required fields — the ONE source the field messages, the summary and the tap all read.
  const missingAddress = address.trim().length === 0;
  // Android acceptance 2026-10-21 (issue 17): the state and the occupancy answer were only reported at
  // Create, one at a time, after the inspector had walked all four steps. They are step-1 facts: they are
  // named here with the rest, all at once, and step 1's Next checks them (a Demo is not a client
  // engagement and needs neither).
  const missingState = !agreement.demo && state === null;
  const missingOccupancy = !agreement.demo && previouslyOccupied === null;
  const unitsBlocked = !agreement.demo && unitCount !== null && unitCount >= 5;
  const missingLayout = areaCount === 0;
  const missingStart = startMode === null;
  const missing = [
    ...(missingAddress ? [t('setup.missingAddress')] : []),
    ...(missingState ? [t('setup.missingState')] : []),
    ...(missingOccupancy ? [t('setup.missingOccupancy')] : []),
    ...(missingLayout ? [t('setup.missingLayout')] : []),
    ...(missingStart ? [t('setup.missingStart')] : [])
  ];
  const step1Missing = [...(missingAddress ? [t('setup.missingAddress')] : []), ...(missingState ? [t('setup.missingState')] : []), ...(missingOccupancy ? [t('setup.missingOccupancy')] : [])];
  // Step 1's Next was tapped while something it needs was missing.
  const [step1Checked, setStep1Checked] = useState(false);
  // Shown only after a tap on Create (or on step 1's Next), and gone the moment the field is fixed (§33 / §35).
  const showValidation = attempted;
  const showStep1Validation = attempted || step1Checked;
  // The licence holder's name is what the agreement and the report print (accountProtocol.ts L-4), so
  // the inspector row shows it instead of offering a name that would be ignored (issue 2).
  const boundHolder = boundHolderName();
  const licenceFixable = gate.kind === 'inspectorStateMissing' || gate.kind === 'stateMismatch';
  // Owner 2026-09-11: creating this inspection SPENDS one of the counted things — a free inspection
  // or a single purchase — and nothing gives it back. A subscription covers unlimited inspections
  // and a Demo is free, so neither is warned about. The warning stands in the form AND is confirmed
  // at the tap, because the charge is the one step here that cannot be undone.
  const coverage = agreement.demo ? null : coverageAvailable({ plan: billing.plan, internalTester: billing.internalTester, singleCredits: billing.singleCredits, trialRemaining: billing.trialRemaining });
  const spendsCount = coverage === 'trial' || coverage === 'single';
  const countTitle = coverage === 'trial' ? t('setup.countTrialTitle') : t('setup.countSingleTitle');
  const countLeftAfter = coverage === 'trial' ? Math.max(0, billing.trialRemaining - 1) : Math.max(0, billing.singleCredits - 1);
  // Issue 4 (Owner misread it 2026-10-20): how many free inspections there are at all, what is left, and
  // the separate 30-day limit on giving one back are three different facts in three sentences.
  const countBody = [
    ...(coverage === 'trial' ? [t('setup.countTrialTotal', { n: FREE_TRIAL_INSPECTIONS })] : []),
    t('setup.countRemaining', { n: countLeftAfter }),
    t('setup.countBody', { limit: RELEASE_LIMIT })
  ].join(' ');
  const year = yearBuilt.trim().length > 0 ? Number.parseInt(yearBuilt.trim(), 10) : null;
  const subject: AgreementSubject = useMemo(
    () => ({
      property: {
        address,
        city,
        state: state ?? '',
        zip,
        stateConfirmed: state !== null,
        previouslyOccupied,
        propertyType,
        unitCount,
        yearBuilt: year !== null && Number.isFinite(year) ? year : null,
        basementType: plan.basement.type,
        basementFinish: plan.basement.finish,
        waterSupplySource,
        wasteDisposalSource,
        heatingFuel
      },
      client: { name: clientName, phone: clientPhone, email: clientEmail },
      inspector: inspectorRefFrom(profile, inspectorName),
      inspectionAt: fromLocalInputValue(inspectionAt, Date.now())
    }),
    [address, city, state, zip, previouslyOccupied, propertyType, year, plan.basement.type, plan.basement.finish, waterSupplySource, wasteDisposalSource, heatingFuel, clientName, clientPhone, clientEmail, profile, inspectorName, inspectionAt]
  );

  const stepTitles: Record<Step, string> = { 1: t('setup.step1Progress'), 2: t('setup.step2Title'), 3: t('layout.title'), 4: t('setup.step4Title') };
  const optional = t('common.optional');
  const required = t('common.required');
  const notSet = t('common.notSet');

  // Step 1's Next stops while a step-1 fact the inspection cannot be created without is missing, names
  // every one of them under its own row at once and brings the first on screen (issue 17).
  function goNext() {
    if (step === 1 && (step1Missing.length > 0 || unitsBlocked)) {
      setStep1Checked(true);
      setFocusTarget(missingAddress ? 'address' : missingState ? 'locality' : missingOccupancy ? 'occupancy' : 'units');
      return;
    }
    setStep((step + 1) as Step);
  }

  // Bring the first thing that stops Create on screen (§34: summary + focus the first invalid field),
  // on the step that holds it.
  function jumpToFirstInvalid() {
    if (missingAddress) {
      setStep(1);
      setFocusTarget('address');
    } else if (missingState) {
      setStep(1);
      setFocusTarget('locality');
    } else if (missingOccupancy) {
      setStep(1);
      setFocusTarget('occupancy');
    } else if (missingLayout) {
      setStep(3);
      setFocusTarget('layout');
    } else if (missingStart) {
      setStep(4);
      setFocusTarget('start');
    } else jumpToGate();
  }

  function jumpToGate() {
    switch (gate.kind) {
      case 'propertyStateUnconfirmed':
      case 'unsupportedPropertyState':
      case 'stateMismatch':
        setStep(1);
        setFocusTarget('locality');
        return;
      case 'occupancyUnconfirmed':
        setStep(1);
        setFocusTarget('occupancy');
        return;
      case 'legalReviewBlock':
        setStep(1);
        setFocusTarget('units');
        return;
      default:
        // the licence is entered right here, over the form (填写执照 next to the message)
        setFocusTarget('blockers');
    }
  }

  useEffect(() => {
    if (!focusTarget || typeof document === 'undefined') return;
    const selector: Record<FocusTarget, string> = {
      address: '#new-address',
      layout: '[data-testid="new-layout"]',
      start: '[data-testid="new-start"]',
      locality: '[data-testid="new-locality"]',
      occupancy: '[data-testid="new-row-occupancy"]',
      units: '[data-testid="new-row-units"]',
      blockers: '[data-testid="new-create-blockers"]'
    };
    const target = document.querySelector<HTMLElement>(selector[focusTarget]);
    setFocusTarget(null);
    if (!target) return;
    if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center' });
    if (target instanceof HTMLInputElement) target.focus();
  }, [focusTarget, step]);

  async function handleCreate(countConfirmed = false) {
    if (submitting.current) return;
    submitting.current = true;
    try {
      await createOnce(countConfirmed);
    } finally {
      submitting.current = false;
    }
  }

  async function createOnce(countConfirmed: boolean) {
    setAttempted(true);
    // The real gates (§38): nothing is created while an ordinary required field or the
    // formal-workflow gate stands. The reason is now on screen, next to what it is about.
    if (missing.length > 0 || gateMessage !== null) {
      setError(null);
      jumpToFirstInvalid();
      return;
    }
    if (areaCount === 0) {
      setError(t('setup.needArea'));
      return;
    }
    if (startMode === null) {
      setError(t('setup.needStartMode'));
      return;
    }
    setBusy(true);
    setError(null);
    // Owner decision D-5 (2026-09-11): without a plan, a new formal inspection is paid for — an
    // active subscription, an unused single purchase, or buy one now. A Demo is always free (X-4),
    // and offline with nothing left is stated as what it is instead of failing at the tap.
    const kind: InspectionKind = agreement.demo ? 'demo' : 'formal';
    const permission = await ensureInspectionAllowed(kind);
    if (permission !== 'allowed') {
      setBusy(false);
      if (permission === 'needsPurchase') setPurchaseOpen(true);
      else setError(permission === 'serverUnavailable' ? t('billing.countsUnavailable') : t('billing.offlineNoCredit'));
      return;
    }
    // The last stop before a count is spent (Owner 2026-09-11).
    if (spendsCount && !countConfirmed) {
      setBusy(false);
      setConfirmCount(true);
      return;
    }
    try {
      const now = Date.now();
      const inspection = await createInspectionAction(
        {
          property: subject.property,
          client: subject.client,
          inspector: subject.inspector,
          weather,
          inspectionAt: fromLocalInputValue(inspectionAt, now),
          startMode,
          agreement
        },
        now,
        plan,
        planNamingFor(locale)
      );
      // The record exists: from here on the draft is never written again, and it is cleared once the
      // cover has been stored on the record.
      draftClosed.current = true;
      rememberInspectorName(inspectorName);
      if (cover) {
        try {
          await setCoverPhotoAction(inspection.id, cover, now);
        } catch (raw) {
          showToast(t('cover.saveFailed', { message: raw instanceof Error ? raw.message : String(raw) }), 'info');
        }
      }
      await clearNewInspectionDraft().catch(() => undefined);
      // The record exists: charge it to the plan or the purchase (idempotent per inspection; a Demo
      // is never charged). A lost answer is reconciled later, never by charging twice.
      if (kind === 'formal') await consumeInspectionCredit(inspection.id);
      if (startMode === 'import') replaceRoute({ screen: 'bulkImport', inspectionId: inspection.id });
      else replaceRoute({ screen: 'inspection', inspectionId: inspection.id });
    } catch (raw) {
      setBusy(false);
      setError(t('common.saveFailed', { message: raw instanceof Error ? raw.message : '' }));
    }
  }

  const localityValue = [city.trim(), state ?? '', zip.trim()].filter((s) => s.length > 0).join(' · ');
  const summaryLine = [city.trim(), state ?? '', zip.trim(), propertyType ? propertyTypeLabel(t, propertyType) : ''].filter((s) => s.length > 0).join(' · ');

  return (
    <div className="screen screen-form wizard" data-testid="new-inspection-screen" data-step={step} data-validation={showValidation ? 'shown' : 'hidden'}>
      <TopBar title={t('setup.title')} onBack={step === 1 ? goBack : () => setStep((step - 1) as Step)} />

      <div className="wizard-progress" data-testid="new-steps">
        <div className="wizard-track" role="tablist" aria-label={t('setup.title')}>
          {STEPS.map((n) => (
            <button
              key={n}
              type="button"
              role="tab"
              aria-selected={n === step}
              aria-label={t('setup.stepOf', { n, total: STEPS.length, title: stepTitles[n] })}
              className={`wizard-seg ${n <= step ? 'wizard-seg-done' : ''} ${n === step ? 'wizard-seg-current' : ''}`}
              onClick={() => setStep(n)}
              data-testid={`new-step-${n}`}
            />
          ))}
        </div>
        <p className="wizard-step-label" data-testid="new-step-label">
          {t('setup.stepOf', { n: step, total: STEPS.length, title: stepTitles[step] })}
        </p>
      </div>

      {draftRestored ? (
        <div className="banner-slot wizard-banner" data-testid="new-draft-note">
          <Banner
            tone="info"
            icon="info"
            title={t('setup.draftRestoredTitle')}
            body={t('setup.draftRestoredBody')}
            action={
              <Button variant="text" size="small" block={false} onClick={startOver} testId="new-draft-discard">
                {t('setup.draftDiscard')}
              </Button>
            }
            testId="new-draft-restored"
          />
        </div>
      ) : null}

      {/* The house this form is about, on every step (Owner-selected design): cover · address · city / state / ZIP / type. */}
      <section className="wizard-summary" data-testid="new-summary">
        <CoverSummary value={cover} onChange={changeCover} />
        <div className="wizard-summary-main">
          <p className={`wizard-summary-address ${address.trim().length === 0 ? 'wizard-summary-address-empty' : ''}`} data-testid="new-summary-address">
            {address.trim().length > 0 ? address.trim() : t('inspection.noAddress')}
          </p>
          {summaryLine.length > 0 ? (
            <p className="wizard-summary-sub" data-testid="new-summary-sub">
              {summaryLine}
            </p>
          ) : null}
        </div>
      </section>

      <div className="screen-body">
        {step === 1 ? (
          <StepCard n={1} title={t('setup.property')} sub={t('setup.step1Sub')} testId="new-property">
            <CoverPhotoField value={cover} onChange={changeCover} testId="new-cover" />
            <div className="form-rows">
              <AddressRow
                id="new-address"
                label={labelWithTag(locale, t('setup.address'), required)}
                value={address}
                onChange={setAddress}
                onResolved={(resolved) => {
                  setAddress(resolved.address);
                  if (resolved.city.length > 0) setCity(resolved.city);
                  if (isJurisdictionCode(resolved.state)) setState(resolved.state);
                  if (resolved.zip.length > 0) setZip(resolved.zip);
                }}
                placeholder={t('setup.addressPlaceholder')}
                error={showStep1Validation && missingAddress}
                testId="new-address"
              />
              {showStep1Validation && missingAddress ? (
                <p className="field-error" role="alert" data-testid="new-address-error">
                  {t('setup.fieldAddressRequired')}
                </p>
              ) : null}
              <button type="button" className="form-row form-row-picker" onClick={() => setLocalityOpen(true)} data-testid="new-locality">
                <span className="form-row-label">{t('setup.localityRow')}</span>
                <span className={`form-row-value ${localityValue.length === 0 ? 'form-row-placeholder' : ''}`} data-testid="new-locality-value">
                  {localityValue.length > 0 ? localityValue : notSet}
                </span>
                <Icon name="chevron_right" className="form-row-chevron" size={20} />
              </button>
              {showStep1Validation && missingState ? (
                <p className="field-error" role="alert" data-testid="new-state-error">
                  {t('setup.needState')}
                </p>
              ) : null}
              <PickerRow
                label={t('setup.propertyType')}
                value={propertyType}
                options={PROPERTY_TYPE_ORDER.map((type) => ({ key: type, label: propertyTypeLabel(t, type), testId: `property-type-${type}` }))}
                onChange={setPropertyType}
                testId="new-row-property-type"
                valueTestId="property-type-value"
              />
              <div className="form-rows-group" data-testid="new-occupancy">
                <PickerRow
                  label={t('gate.occupancyTitle')}
                  value={previouslyOccupied === null ? null : previouslyOccupied ? 'yes' : 'no'}
                  options={[
                    { key: 'yes', label: t('common.yes'), testId: 'occupancy-yes' },
                    { key: 'no', label: t('common.no'), testId: 'occupancy-no' }
                  ]}
                  onChange={(key) => setPreviouslyOccupied(key === null ? null : key === 'yes')}
                  testId="new-row-occupancy"
                  valueTestId="new-occupancy-value"
                />
                {showStep1Validation && missingOccupancy ? (
                  <p className="field-error" role="alert" data-testid="new-occupancy-error">
                    {t('setup.needOccupancy')}
                  </p>
                ) : null}
                <p className="card-note form-rows-note">{t('gate.occupancyBody')}</p>
                {previouslyOccupied === false ? (
                  <p className="card-note form-rows-note" data-testid="new-build-hint">
                    {t('setup.newBuildHint')}
                  </p>
                ) : null}
              </div>
              {/* CK-10 (NY F1 Round 5): one to four dwelling units are served; five or more is a
                  legal-review block, correctable here. "Not asked" is never a block. */}
              <div className="form-rows-group" data-testid="new-units">
                <PickerRow
                  label={t('setup.unitCount')}
                  value={unitCount === null ? null : unitCount >= 5 ? '5' : String(unitCount)}
                  options={[
                    ...[1, 2, 3, 4].map((n) => ({ key: String(n), label: String(n), testId: `new-units-${n}` })),
                    { key: '5', label: t('setup.unitCountOverLimit'), testId: 'new-units-5' }
                  ]}
                  onChange={(key) => setUnitCount(key === null ? null : Number.parseInt(key, 10))}
                  testId="new-row-units"
                  valueTestId="new-units-value"
                />
                {showStep1Validation && unitsBlocked ? (
                  <p className="field-error" role="alert" data-testid="new-units-error">
                    {t('setup.unitCountBlocked')}
                  </p>
                ) : null}
                <p className="card-note form-rows-note">{t('setup.unitCountHint')}</p>
              </div>
              <PropertyFactRows
                heatingFuel={heatingFuel}
                waterSupplySource={waterSupplySource}
                wasteDisposalSource={wasteDisposalSource}
                onChange={(patch) => {
                  if (patch.heatingFuel !== undefined) setHeatingFuel(patch.heatingFuel);
                  if (patch.waterSupplySource !== undefined) setWaterSupplySource(patch.waterSupplySource);
                  if (patch.wasteDisposalSource !== undefined) setWasteDisposalSource(patch.wasteDisposalSource);
                }}
                testId="new-facts"
              />
              <RowInput id="new-year" label={labelWithTag(locale, t('setup.yearBuilt'), optional)} value={yearBuilt} onChange={setYearBuilt} placeholder={t('setup.yearPlaceholder')} inputMode="numeric" testId="new-year" />
            </div>
          </StepCard>
        ) : null}

        {step === 2 ? (
          <StepCard n={2} title={t('setup.step2Title')} sub={t('setup.step2Sub')} testId="new-client-card">
            <div className="form-rows" data-testid="new-client-rows">
              <RowInput id="new-client" label={labelWithTag(locale, t('setup.clientName'), optional)} value={clientName} onChange={setClientName} placeholder={t('setup.clientNamePlaceholder')} testId="new-client" />
              <RowInput id="new-phone" label={labelWithTag(locale, t('setup.clientPhone'), optional)} value={clientPhone} onChange={setClientPhone} inputMode="tel" placeholder={t('setup.clientPhonePlaceholder')} testId="new-phone" />
              <RowInput id="new-client-email" label={labelWithTag(locale, t('setup.clientEmail'), optional)} value={clientEmail} onChange={setClientEmail} inputMode="email" type="email" placeholder={t('setup.clientEmailPlaceholder')} testId="new-client-email" />
            </div>
            <div className="form-rows" data-testid="new-schedule">
              <FormRow label={t('setup.inspectionAt')} className="form-row-datetime">
                <input id="new-inspection-at" className="form-row-field" type="datetime-local" value={inspectionAt} onChange={(event) => setInspectionAt(event.target.value)} aria-label={t('setup.inspectionAt')} data-testid="new-inspection-at" />
              </FormRow>
              {boundHolder ? (
                <FormRow label={t('setup.inspector')}>
                  <span className="form-row-value" data-testid="new-inspector-bound">
                    {boundHolder}
                  </span>
                </FormRow>
              ) : (
                <RowInput id="new-inspector" label={t('setup.inspector')} value={inspectorName} onChange={setInspectorName} placeholder={t('setup.inspectorPlaceholder')} testId="new-inspector" />
              )}
              <RowInput id="new-weather" label={labelWithTag(locale, t('setup.weather'), optional)} value={weather} onChange={setWeather} placeholder={t('setup.weatherPlaceholder')} testId="new-weather" />
            </div>
            {boundHolder ? (
              <p className="card-note" data-testid="new-inspector-note">
                {t('setup.inspectorFromLicence')}
              </p>
            ) : null}
          </StepCard>
        ) : null}

        {step === 3 ? (
          <StepCard n={3} title={t('layout.title')} sub={t('setup.step3Sub')} testId="new-layout-step">
            <PropertyLayoutCard plan={plan} onChange={setPlan} propertyType={propertyType} embedded testId="new-layout" />
            {showValidation && missingLayout ? (
              <p className="field-error" role="alert" data-testid="new-layout-error">
                {t('setup.fieldLayoutRequired')}
              </p>
            ) : null}
          </StepCard>
        ) : null}

        {step === 4 ? (
          <>
            <StepCard n={4} title={t('setup.step4Title')} sub={t('setup.step4Sub')} testId="new-agreement-step">
              <AgreementCard agreement={agreement} subject={subject} onChange={setAgreement} embedded testId="new-agreement" />
              {/* X-1 (Owner default 2026-09-11): booking first is allowed; field work is not. Say it here
                  instead of refusing the creation. */}
              {!agreementSigned(agreement) ? (
                <p className="card-note" data-testid="new-agreement-later">
                  {t('setup.agreementLater')}
                </p>
              ) : null}
              <div className="wizard-subsection" data-testid="new-start">
                <h3 className="wizard-subtitle">{t('setup.startTitle')}</h3>
                <div className="choice-stack">
                  <ChoiceCard icon="perm_media" title={t('setup.startImportTitle')} body={t('setup.startImportBody')} selected={startMode === 'import'} onClick={() => setStartMode('import')} testId="start-import" />
                  <ChoiceCard icon="checklist" title={t('setup.startLiveTitle')} body={t('setup.startLiveBody')} selected={startMode === 'live'} onClick={() => setStartMode('live')} testId="start-live" />
                </div>
                <p className="card-note">{t('setup.startBody')}</p>
                {/* Owner 2026-10-20: an unfinished inspection lives on ONE device; say so before it exists. */}
                <p className="card-note" data-testid="new-device-note">
                  {t('setup.deviceNote')}
                </p>
                {showValidation && missingStart ? (
                  <p className="field-error" role="alert" data-testid="new-start-error">
                    {t('setup.fieldStartRequired')}
                  </p>
                ) : null}
              </div>
            </StepCard>
            {spendsCount ? (
              <div className="banner-slot wizard-banner">
                <Banner tone="warning" icon="info" title={countTitle} body={countBody} testId="new-count-warning" />
              </div>
            ) : null}
            <p className="hint">{t('setup.hint')}</p>
          </>
        ) : null}
      </div>

      <div ref={footerRef} className="sticky-actions form-footer" data-testid="new-actions">
        {!showValidation && step === 1 && step1Checked && step1Missing.length > 0 ? (
          <div className="create-blockers" role="status" data-testid="new-step1-blockers">
            <button type="button" className="create-blocker create-blocker-jump" onClick={goNext} title={t('setup.missingJump')} data-testid="new-step1-missing">
              {t('setup.fixBeforeCreate', { list: step1Missing.join(' · ') })}
            </button>
          </div>
        ) : null}
        {showValidation && (missing.length > 0 || gateMessage !== null || error) ? (
          <div className="create-blockers" role="status" data-testid="new-create-blockers">
            {missing.length > 0 ? (
              <button type="button" className="create-blocker create-blocker-jump" onClick={jumpToFirstInvalid} title={t('setup.missingJump')} data-testid="new-create-missing">
                {t('setup.fixBeforeCreate', { list: missing.join(' · ') })}
              </button>
            ) : null}
            {gateMessage !== null ? (
              <div className="create-blocker-row">
                <button type="button" className="create-blocker create-blocker-jump" onClick={jumpToGate} title={t('setup.missingJump')} data-testid="new-gate">
                  {gateMessage}
                </button>
                {licenceFixable ? (
                  <Button variant="secondary" size="small" block={false} onClick={() => setLicenceOpen(true)} testId="new-fill-licence">
                    {t('setup.fillLicence')}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {error ? (
              <p className="create-blocker create-blocker-error" data-testid="new-error">
                {error}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="btn-row">
          <Button variant="text" block={false} onClick={goBack}>
            {t('shell.cancel')}
          </Button>
          {step < 4 ? (
            <Button onClick={goNext} trailingIcon="arrow_forward" testId="new-next">
              {t('setup.next')}
            </Button>
          ) : (
            <Button onClick={() => void handleCreate()} disabled={busy} sub={tp('setup.createSub', areaCount)} testId="create-inspection">
              {startMode === 'import' ? t('setup.createImport') : t('setup.create')}
            </Button>
          )}
        </div>
        {/* Owner-frozen AI notice (2026-09-13 §29 / §30 B): always under the form's action, short, red. */}
        <p className="ai-disclaimer" data-testid="create-ai-disclaimer">
          {t('ai.disclaimer')}
        </p>
      </div>

      {localityOpen ? (
        <SheetShell
          title={t('setup.localityTitle')}
          onClose={() => setLocalityOpen(false)}
          // iPhone run 2026-10-26 #6: what is typed here is kept as it is typed, so the header button is the same
          // 完成 as the footer's — reachable at the top of the sheet whatever the keyboard covers.
          closeLabel={t('shell.done')}
          testId="new-locality-sheet"
          footer={
            <Button onClick={() => setLocalityOpen(false)} testId="new-locality-done">
              {t('shell.done')}
            </Button>
          }
        >
          <div className="form-stack">
            <Field label={t('setup.city')} htmlFor="new-city">
              <TextInput id="new-city" value={city} onChange={setCity} placeholder={t('setup.cityPlaceholder')} testId="new-city" />
            </Field>
            <Field label={t('setup.state')} htmlFor="new-state">
              <select id="new-state" className="field-input" value={state ?? ''} onChange={(event) => setState(isJurisdictionCode(event.target.value) ? event.target.value : null)} data-testid="new-state">
                <option value="">{t('setup.statePlaceholder')}</option>
                {JURISDICTION_CODE_ORDER.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('setup.zip')} htmlFor="new-zip">
              <TextInput id="new-zip" value={zip} onChange={setZip} placeholder="11354" inputMode="numeric" testId="new-zip" />
            </Field>
          </div>
        </SheetShell>
      ) : null}

      {/* D-5: nothing is created until the inspection is paid for; buying here returns to this form
          with everything still filled in, and the inspector taps Create again. */}
      {purchaseOpen ? <PurchaseSheet onClose={() => setPurchaseOpen(false)} onPurchased={() => setPurchaseOpen(false)} /> : null}

      {licenceOpen ? (
        <LicenceSheet
          onSaved={(next) => {
            setProfile(next);
            setLicenceOpen(false);
          }}
          onClose={() => setLicenceOpen(false)}
        />
      ) : null}

      {confirmCount ? (
        <ConfirmSheet
          title={countTitle}
          body={countBody}
          confirmLabel={t('setup.countConfirm')}
          onConfirm={() => {
            setConfirmCount(false);
            void handleCreate(true);
          }}
          onCancel={() => setConfirmCount(false)}
          testId="count-confirm"
        />
      ) : null}
    </div>
  );
}
